import { useTranslation } from 'react-i18next'
import { WEEKDAYS, type TimeWindow, type Weekday } from '../types'

type DayMode = 'free' | 'closed' | 'window'

interface TimeWindowsEditorProps {
  readonly value: Partial<Record<Weekday, TimeWindow[]>>
  readonly onChange: (value: Partial<Record<Weekday, TimeWindow[]>>) => void
}

/**
 * Trois états par jour, qui reprennent exactement la lecture du serveur :
 * jour absent de la configuration (libre), jour déclaré sans créneau (fermé),
 * ou jour ouvert sur une plage horaire.
 */
function modeOf(windows: TimeWindow[] | undefined): DayMode {
  if (windows === undefined) return 'free'

  return windows.length === 0 ? 'closed' : 'window'
}

const DEFAULT_WINDOW: TimeWindow = { start: '16:00', end: '19:00' }

const FIELD =
  'rounded-lg border border-surface-200 px-2 py-1.5 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'

export function TimeWindowsEditor({ value, onChange }: TimeWindowsEditorProps) {
  const { t } = useTranslation()

  function setMode(day: Weekday, mode: DayMode) {
    const next = { ...value }

    if (mode === 'free') {
      delete next[day]
    } else {
      next[day] = mode === 'closed' ? [] : [{ ...DEFAULT_WINDOW }]
    }

    onChange(next)
  }

  function setBound(day: Weekday, bound: 'start' | 'end', time: string) {
    const current = value[day]?.[0] ?? DEFAULT_WINDOW

    onChange({ ...value, [day]: [{ ...current, [bound]: time }] })
  }

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-sm font-medium text-surface-800">
        {t('parent.restrictions.hours')}
      </legend>
      <p className="mb-2 text-xs text-on-surface-variant">{t('parent.restrictions.hours_hint')}</p>

      {WEEKDAYS.map((day) => {
        const mode = modeOf(value[day])
        const window = value[day]?.[0] ?? DEFAULT_WINDOW

        return (
          <div key={day} className="flex flex-wrap items-center gap-2 rounded-xl bg-surface-low px-3 py-2">
            <span className="w-24 shrink-0 text-sm font-medium text-on-surface">
              {t(`parent.restrictions.days.${day}`)}
            </span>

            <select
              value={mode}
              onChange={(event) => setMode(day, event.target.value as DayMode)}
              aria-label={t('parent.restrictions.day_mode', { day: t(`parent.restrictions.days.${day}`) })}
              className={FIELD}
            >
              <option value="free">{t('parent.restrictions.modes.free')}</option>
              <option value="window">{t('parent.restrictions.modes.window')}</option>
              <option value="closed">{t('parent.restrictions.modes.closed')}</option>
            </select>

            {mode === 'window' && (
              <span className="flex items-center gap-2">
                <input
                  type="time"
                  value={window.start}
                  onChange={(event) => setBound(day, 'start', event.target.value)}
                  aria-label={t('parent.restrictions.from', { day: t(`parent.restrictions.days.${day}`) })}
                  className={FIELD}
                />
                <input
                  type="time"
                  value={window.end}
                  onChange={(event) => setBound(day, 'end', event.target.value)}
                  aria-label={t('parent.restrictions.to', { day: t(`parent.restrictions.days.${day}`) })}
                  className={FIELD}
                />
              </span>
            )}
          </div>
        )
      })}
    </fieldset>
  )
}
